import React, { useState, useMemo } from "react";
import { Modal } from "react-bootstrap";

import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import InputField from "../../../../../components/InputField";
import Button from "../../../../../components/Button";
import Table from "../../../../../components/table";
import BackButton from "../../../../../components/BackButton";
import SendIcon from "@mui/icons-material/Send";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Select from "react-select";
import api from "../../../../../api/api";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { INITIAL_VENDOR_TEMPLATE, selectPortalStyles } from "./constants";
import useCommercialData from "./hooks/useCommercialData";
import useVendorState from "./hooks/useVendorState";
import usePurchaseInputRow from "./hooks/usePurchaseInputRow";
import SalesSection from "./components/SalesSection";
import PurchaseChargeTable from "./components/PurchaseChargeTable";
import VendorPurchaseForm from "./components/VendorPurchaseForm";
import ConfirmModals from "./components/ConfirmModals";
import SelectField from "../../../../../components/SelectField";
import Pagination from "../../../../../components/Pagination";
import usePagination from "../../../../../utils/usePagination";

const Commercial = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const buildPurchaseUpdateEndpoint = (purchaseInvoiceId) =>
    `/purchase/${purchaseInvoiceId}`;

  // =========================================================
  // VIEW MODAL STATE
  // =========================================================
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorIndex, setSelectedVendorIndex] = useState(null);
  const [showVendorViewModal, setShowVendorViewModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [editVendor, setEditVendor] = useState(null);

  // Draft row for adding a new charge in the Edit Vendor Purchase modal
  const EMPTY_VENDOR_CHARGE = {
    charge_code: "",
    sac_code: "",
    currency: "INR",
    rate: "",
    quantity: "",
    taxable_amount: 0,
    gst_applicable: "No",
    gst_percent: 0,
    gst_amount: 0,
  };

  const [vendorChargeDraft, setVendorChargeDraft] = useState({
    ...EMPTY_VENDOR_CHARGE,
  });

  const [vendorChargeError, setVendorChargeError] = useState("");

  // Data fetching hook
  const {
    enquiry,
    loading,
    containerOptions,
    vendorOptions,
    loadingVendors,
    chargeCodeOptions,
    currencyOptions,
    loadingCurrencies,
    loadingChargeCodes,
    initialVendors,
    initialPurchases,
    portOptions,
    loadingPorts,
    containerTableData,
    fetchGSTForChargeCode,
  } = useCommercialData(id);

  // Vendor state hook
  const {
    vendors,
    setVendors,
    vendorInvoiceDates,
    setVendorInvoiceDates,
    errors,
    addVendor,
    removeVendor,
    updateVendor,
    updateVendorInputCharge,
    handleVendorChargeInputChange,
    addChargeRow,
    deleteChargeRow,
    handleVendorSelect,
  } = useVendorState(initialVendors, containerOptions);

  // Purchase input row hook
  const { getPurchaseInputRow, updatePurchaseInputRow, addPurchaseChargeRow } =
    usePurchaseInputRow(containerOptions, chargeCodeOptions);

  // UI state
  const [selectedVendorIndices, setSelectedVendorIndices] = useState([]);
  const [activeKeys, setActiveKeys] = useState(["0", "1"]);
  const [showSaveAllModal, setShowSaveAllModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [postingToTally, setPostingToTally] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");

  //   const purchasePagination = usePagination(
  //   formik.values.purchases || [],
  //   5
  // );
  const [formData] = useState({
    purchase_invoice_number: "",
    purchase_invoice_date: "",
  });

  // Auth
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const allowedEditors = ["GFR003", "GFR009", "wMe2inT9YF9i"];

  const canEdit = allowedEditors.includes(user?.user_id);

  const isAdminOrSuperAdmin = ["admin", "superadmin", "GFR003"].includes(
    user?.role || user?.user_id,
  );

  const isSuperAdmin = user?.user_role === "SuperAdmin";

  // =========================================================
  // FORMIK
  // =========================================================
  const formik = useFormik({
    enableReinitialize: true,

    initialValues: enquiry
      ? {
          ...enquiry,
          sales_invoice_no: enquiry.sales_invoice_no || "",
          sales_amount: enquiry.sales_amount || 0,
          sales_currency: enquiry.sales_currency || "INR",
          sales_remarks: enquiry.sales_remarks || "",
          purchases: initialPurchases || [],
        }
      : {
          sales_invoice_no: "",
          sales_amount: 0,
          sales_currency: "INR",
          sales_remarks: "",
          purchases: initialPurchases || [],
        },

    onSubmit: () => {
      setShowConfirm(true);
    },
  });

  const filteredPurchases = useMemo(() => {
    const all = formik.values.purchases || [];
    const term = vendorSearch.trim().toLowerCase();

    if (!term) {
      return all;
    }

    return all.filter((p) =>
      (p.vendor_name || "").toString().toLowerCase().includes(term),
    );
  }, [formik.values.purchases, vendorSearch]);

  const purchasePagination = usePagination(filteredPurchases, 5);

  // =========================================================
  // HANDLERS
  // =========================================================

  const handleSelectVendor = (index) => {
    const key = `vendor-${index}`;

    setSelectedVendorIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );

    setActiveKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleRemoveVendor = (index) => {
    setSelectedVendorIndices((prev) => prev.filter((i) => i !== index));

    const key = `vendor-${index}`;

    setActiveKeys((prev) => prev.filter((k) => k !== key));
  };

  const handleRemoveItemRow = (vendorIdx, itemIdx) => {
    const updated = [...formik.values.purchases];

    if (!updated[vendorIdx]) return;

    updated[vendorIdx].items.splice(itemIdx, 1);

    formik.setFieldValue("purchases", updated);
  };

  const fetchGstPercent = async (chargeCode) => {
    if (!chargeCode) return 0;

    try {
      const res = await api.get(
        `/charge_code/gst/${encodeURIComponent(chargeCode)}`,
      );

      return Number(res.data?.tax_percentage) || 0;
    } catch (err) {
      console.error("Failed to fetch GST for charge code:", err);

      return 0;
    }
  };

  const handleUpdateItemRow = (
    vendorIdx,
    itemIdx,
    field,
    value,
    extraFields,
  ) => {
    const updated = [...formik.values.purchases];

    if (!updated[vendorIdx]) return;

    const item = {
      ...updated[vendorIdx].items[itemIdx],
      [field]: value,
    };

    // Apply extra fields
    if (extraFields) {
      Object.assign(item, extraFields);
    }

    // Auto calculate taxable amount
    if (field === "rate" || field === "quantity") {
      const rate = field === "rate" ? Number(value) : Number(item.rate || 0);

      const quantity =
        field === "quantity" ? Number(value) : Number(item.quantity || 0);

      item.taxable_amount = (rate * quantity).toString();

      const gstPct = Number(item.gst_percent) || 0;

      if (item.gst_applicable === "Yes" && gstPct > 0) {
        item.gst_amount = ((rate * quantity * gstPct) / 100).toFixed(2);
      }
    }

    // Recalculate GST
    if (field === "gst_percent") {
      const taxable = Number(item.taxable_amount) || 0;

      const gstPct = Number(value) || 0;

      if (item.gst_applicable === "Yes" && gstPct > 0) {
        item.gst_amount = ((taxable * gstPct) / 100).toFixed(2);
      } else {
        item.gst_amount = 0;
      }
    }

    if (
      field === "charge_code" &&
      extraFields &&
      "gst_percent" in extraFields
    ) {
      const taxable = Number(item.taxable_amount) || 0;

      const gstPct = Number(item.gst_percent) || 0;

      item.gst_amount =
        item.gst_applicable === "Yes" && gstPct > 0
          ? ((taxable * gstPct) / 100).toFixed(2)
          : 0;
    }

    // GST No
    if (field === "gst_applicable" && value === "No") {
      item.gst_percent = 0;
      item.gst_amount = 0;
    }

    // GST Yes
    if (
      field === "gst_applicable" &&
      value === "Yes" &&
      extraFields &&
      "gst_percent" in extraFields
    ) {
      const taxable = Number(item.taxable_amount) || 0;

      const gstPct = Number(item.gst_percent) || 0;

      item.gst_amount = gstPct > 0 ? ((taxable * gstPct) / 100).toFixed(2) : 0;
    }

    updated[vendorIdx] = {
      ...updated[vendorIdx],

      items: updated[vendorIdx].items.map((it, idx) =>
        idx === itemIdx ? item : it,
      ),
    };

    formik.setFieldValue("purchases", updated);
  };

  const handleSaveAllPurchases = async () => {
    try {
      setSaving(true);
      setShowSaveAllModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePostToTally = async (e, rowData) => {
    e.stopPropagation();

    setPostingToTally(true);

    try {
      await api.post("/tally/post-purchase", {
        purchase_data: rowData,
        enquiry_id: id,
      });

      alert("Successfully posted to Tally!");

      const updatedPurchases = formik.values.purchases.map((p) =>
        p.id === rowData.id
          ? {
              ...p,
              status: "Posted",
            }
          : p,
      );

      formik.setFieldValue("purchases", updatedPurchases);
    } catch (err) {
      console.error("Error posting to Tally:", err);

      alert("Failed to post data to Tally.");
    } finally {
      setPostingToTally(false);
    }
  };

  const handleConfirm = async () => {
    setLoadingSubmit(true);

    try {
      setShowConfirm(false);

      await api.put(`/master-data/container-status-report`, {
        ...formik.values,
        enquiry_id: id,
      });
    } catch (error) {
      console.error("Error updating commercial data:", error);

      alert("Failed to update commercial data.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  // =========================================================
  // VIEW MODAL
  // =========================================================

  const makeEditCopy = (row) => ({
    ...row,
    items: Array.isArray(row?.items) ? row.items.map((it) => ({ ...it })) : [],
  });

  const handleView = (row, index) => {
    setSelectedVendor(row);
    setSelectedVendorIndex(index);
    setEditVendor(null);
    setModalMode("view");
    setShowVendorViewModal(true);
  };

  const handleEdit = (row, index) => {
    setSelectedVendor(row);
    setSelectedVendorIndex(index);
    setEditVendor(makeEditCopy(row));
    setModalMode("edit");
    setShowVendorViewModal(true);
  };

  // Update a header field on the working copy (edit mode)
  const handleEditFieldChange = (field, value) => {
    setEditVendor((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  // Update a charge-item field on the working copy, recomputing taxable + GST
  const handleEditItemChange = (itemIndex, field, value) => {
    setEditVendor((prev) => {
      if (!prev) return prev;

      const items = [...(prev.items || [])];
      const item = { ...items[itemIndex], [field]: value };

      // Direct edits to Taxable Amount / GST Amount are kept as-is (no recompute).
      if (field === "taxable_amount" || field === "gst_amount") {
        items[itemIndex] = item;
        return { ...prev, items };
      }

      // Recalculate taxable amount
      if (field === "rate" || field === "quantity") {
        const rate = field === "rate" ? Number(value) : Number(item.rate || 0);
        const quantity =
          field === "quantity" ? Number(value) : Number(item.quantity || 0);

        item.taxable_amount = (rate * quantity).toString();

        const gstPct = Number(item.gst_percent) || 0;
        if (item.gst_applicable === "Yes" && gstPct > 0) {
          item.gst_amount = ((rate * quantity * gstPct) / 100).toFixed(2);
        }
      }

      // Recalculate GST on percent change
      if (field === "gst_percent") {
        const taxable = Number(item.taxable_amount) || 0;
        const gstPct = Number(value) || 0;
        item.gst_amount =
          item.gst_applicable === "Yes" && gstPct > 0
            ? ((taxable * gstPct) / 100).toFixed(2)
            : 0;
      }

      // GST toggled to No
      if (field === "gst_applicable" && value === "No") {
        item.gst_percent = 0;
        item.gst_amount = 0;
      }

      // GST toggled to Yes
      if (field === "gst_applicable" && value === "Yes") {
        const taxable = Number(item.taxable_amount) || 0;
        const gstPct = Number(item.gst_percent) || 0;
        item.gst_amount =
          gstPct > 0 ? ((taxable * gstPct) / 100).toFixed(2) : 0;
      }

      items[itemIndex] = item;
      return { ...prev, items };
    });
  };

  // Recompute taxable + GST amounts for a charge object (used by the draft row)
  const recomputeVendorCharge = (row) => {
    const next = { ...row };

    const rate = Number(next.rate) || 0;
    const quantity = Number(next.quantity) || 0;

    next.taxable_amount = (rate * quantity).toString();

    const gstPct = Number(next.gst_percent) || 0;

    if (next.gst_applicable === "Yes" && gstPct > 0) {
      next.gst_amount = ((rate * quantity * gstPct) / 100).toFixed(2);
    } else {
      next.gst_amount = 0;
    }

    return next;
  };

  // Update a field on the draft (new charge) row
  const handleVendorChargeDraftChange = async (field, value) => {
    setVendorChargeDraft((prev) => {
      let updated = { ...prev, [field]: value };

      if (field === "gst_applicable" && value === "No") {
        updated.gst_percent = 0;
      }

      return recomputeVendorCharge(updated);
    });

    // Fetch GST % when the charge code changes
    if (field === "charge_code") {
      const opt = chargeCodeOptions.find((c) => c.value === value);
      const chargeLabel = opt?.label || value;

      if (opt?.sac_code) {
        setVendorChargeDraft((prev) => ({
          ...prev,
          sac_code: opt.sac_code,
        }));
      }

      const taxPct = await fetchGstPercent(chargeLabel);

      setVendorChargeDraft((prev) =>
        recomputeVendorCharge({
          ...prev,
          gst_percent: prev.gst_applicable === "Yes" ? taxPct : 0,
        }),
      );
    }

    // Re-fetch GST % when GST is toggled to Yes
    if (field === "gst_applicable" && value === "Yes") {
      const current = vendorChargeDraft.charge_code;
      const opt = chargeCodeOptions.find((c) => c.value === current);
      const chargeLabel = opt?.label || current;
      const taxPct = await fetchGstPercent(chargeLabel);

      setVendorChargeDraft((prev) =>
        recomputeVendorCharge({ ...prev, gst_percent: taxPct }),
      );
    }
  };

  // Commit the draft row into editVendor.items, then reset the draft
  const handleAddVendorChargeRow = () => {
    if (!vendorChargeDraft.charge_code) {
      setVendorChargeError("Please select a charge code before adding.");
      return;
    }

    const newRow = recomputeVendorCharge(vendorChargeDraft);

    setEditVendor((prev) =>
      prev ? { ...prev, items: [...(prev.items || []), newRow] } : prev,
    );

    setVendorChargeDraft({ ...EMPTY_VENDOR_CHARGE });
    setVendorChargeError("");
  };

  // Remove the charge row at the given index (edit mode)
  const handleRemoveVendorChargeRow = (itemIndex) => {
    setEditVendor((prev) => {
      if (!prev) return prev;

      const items = (prev.items || []).filter((_, idx) => idx !== itemIndex);

      return { ...prev, items };
    });
  };

  const handleSaveVendorEdit = async () => {
    if (!editVendor) return;

    setSaving(true);
    try {
      // Map working copy back to the backend payload shape
      // (inverse of the mapping in useCommercialData).
      const payload = {
        enq_id: id,
        purchase_invoice_id: editVendor.purchase_invoice_id,
        purchase_invoice_number: editVendor.purchase_invoice_number,
        vendorEntries: [
          {
            vendor_id: editVendor.vendor_id,
            vendor_name: editVendor.vendor_name,
            vendor_location: editVendor.vendor_location,
            party_gstin: editVendor.gst_number,
            pan_number: editVendor.pan_number,
            voucher_invoice_number: editVendor.voucher_invoice_number,
            voucher_invoice_date: editVendor.vendor_invoice_date,
            vb_number: editVendor.vb_number,
            vendor_job_number: editVendor.vendor_job_number,
            shipper: editVendor.master_shipper,
            consignee: editVendor.master_consignee,
            mbl_number: editVendor.master_bl_number,
            vessel_name: editVendor.vessel_name,
            vessel_number: editVendor.vessel_number,
            voyage_number: editVendor.voyage_number,
            etd_date: editVendor.estimated_date,
            cut_off_date: editVendor.cut_off_date,
            charges: (editVendor.items || []).map((c) => ({
              charge_code: c.charge_code,
              sac_code: c.sac_code,
              currency: c.currency,
              rate: c.rate,
              quantity: c.quantity,
              taxable_amount: c.taxable_amount,
              gst_applicable: c.gst_applicable || "Yes",
              gst_percentage: c.gst_percent,
              gst_amount: c.gst_amount,
            })),
          },
        ],
      };

      // TODO: swap endpoint for the real one once confirmed.
      await api.put(
        buildPurchaseUpdateEndpoint(editVendor.purchase_invoice_id),
        payload,
      );

      // Reflect the change in the table state
      const updated = [...(formik.values.purchases || [])];
      if (selectedVendorIndex != null && updated[selectedVendorIndex]) {
        updated[selectedVendorIndex] = { ...editVendor };
        formik.setFieldValue("purchases", updated);
      }
      handleCloseVendorViewModal();
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to save vendor purchase edit:", error);
      alert("Failed to save vendor purchase details.");
    } finally {
      setSaving(false);
    }
  };

  const handleCloseVendorViewModal = () => {
    setShowVendorViewModal(false);
    setSelectedVendor(null);
    setSelectedVendorIndex(null);
    setEditVendor(null);
    setVendorChargeDraft({ ...EMPTY_VENDOR_CHARGE });
    setVendorChargeError("");
    setModalMode("view");
  };

  // =========================================================
  // PURCHASE COLUMNS
  // =========================================================

  const purchaseColumns = [
    {
      label: "S.No.",
      render: (_, index) => index + 1,
    },

    {
      label: "Vendor Name",

      render: (row, idx) => (
        <span>{row.vendor_name || `Vendor ${idx + 1}`}</span>
      ),
    },

    {
      label: "Vendor Job No.",
      accessor: "vendor_job_number",
    },

    {
      label: "Vendor Invoice No.",

      render: (row) => row.voucher_invoice_number || row.invoice_no || "N/A",
    },

    {
      label: "Booking No.",

      render: (row) => row.vb_number || row.booking_no || "N/A",
    },

    // =====================================================
    // ACTION
    // =====================================================

    {
      label: "Action",

      render: (row, index) => (
        <div className="d-flex justify-content-center gap-2">
          {/* VIEW */}
          <Button
            variant="outline-secondary"
            size="sm"
            title="View"
            onClick={(e) => {
              e.stopPropagation();
              handleView(row, index);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </Button>

          {/* EDIT */}
          <Button
            variant="outline-secondary"
            size="sm"
            title="Edit"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row, index);
            }}
          >
            <EditIcon fontSize="small" />
          </Button>
        </div>
      ),
    },

    // =====================================================
    // TALLY
    // =====================================================

    ...(isAdminOrSuperAdmin
      ? [
          {
            label: "Tally Post",

            render: (row) => (
              <Button
                variant="outline-success"
                size="sm"
                disabled={postingToTally || row.status === "Posted"}
                onClick={(e) => handlePostToTally(e, row)}
              >
                <SendIcon fontSize="small" /> Post
              </Button>
            ),
          },
        ]
      : []),
  ];

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div>
      <BackButton />

      <form onSubmit={formik.handleSubmit}>
        <Accordion
          activeKey={activeKeys}
          alwaysOpen
          onSelect={(eventKeys) => setActiveKeys(eventKeys || [])}
          className="customaccordion mb-4"
        >
          {/* =================================================
              SECTION 1: SALES
          ================================================= */}

          <SalesSection />

          {/* =================================================
              SECTION 2: ALL PURCHASES
          ================================================= */}

          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <span className="fw-bold text-black">
                All Purchases (Vendor Invoices)
              </span>
            </Accordion.Header>

            <Accordion.Body>
              <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
                {/* Search Bar */}
                <div className="flex-grow-1 position-relative">
                  <input
                    type="text"
                    className="form-control ps-5"
                    placeholder="Search By Vendor Name..."
                    value={vendorSearch}
                    onChange={(e) => {
                      setVendorSearch(e.target.value);
                      purchasePagination.goToPage(1);
                    }}
                  />

                  <SearchIcon
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#6b7280",
                      width: "17px",
                      height: "17px",
                    }}
                  />
                </div>

                {/* Buttons */}
                {isSuperAdmin && (
                  <div className="d-flex align-items-center gap-3">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSaveAllModal(true);
                      }}
                      btncls="mt-0"
                    >
                      Save All Purchases
                    </Button>

                    <Button
                      variant="contained"
                      btncls="bg-darkblue text-white px-4 py-2 vendor-add-btn mt-0"
                      onClick={addVendor}
                      startIcon={<AddCircleOutlineIcon />}
                    >
                      Add New Vendor
                    </Button>
                  </div>
                )}
              </div>

              {/* =================================================
                  PURCHASE TABLE
              ================================================= */}
              <Table
                columns={purchaseColumns}
                data={purchasePagination.currentItems}
                emptyMessage="No purchase records found."
              />

              <Pagination
                currentPage={purchasePagination.currentPage}
                totalPages={purchasePagination.totalPages}
                onPageChange={purchasePagination.goToPage}
                itemsPerPage={purchasePagination.itemsPerPage}
                onItemsPerPageChange={
                  purchasePagination.handleItemsPerPageChange
                }
                totalItems={purchasePagination.totalItems}
              />

              {/* =================================================
                  VENDOR PURCHASE DETAILS ACCORDION
              ================================================= */}

              {formik.values.purchases?.map((vendor, vendorIdx) => {
                if (!selectedVendorIndices.includes(vendorIdx)) {
                  return null;
                }

                return (
                  <Accordion.Item
                    eventKey={`vendor-${vendorIdx}`}
                    key={vendor.id || vendorIdx}
                  >
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <span className="fw-bold text-black">
                          As Per Vendor {vendorIdx + 1} (
                          {vendor.vendor_name || "New Vendor"})
                        </span>
                      </div>
                    </Accordion.Header>

                    <Accordion.Body>
                      {/* =================================================
                            PURCHASE INVOICE DETAILS
                        ================================================= */}

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold text-secondary m-0">
                          Purchase Invoice Details
                        </h6>

                        {canEdit && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveVendor(vendorIdx)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      {/* =================================================
                            VENDOR METADATA
                        ================================================= */}

                      <div className="row g-3">
                        {[
                          {
                            label: "Vendor Name",
                            field: "vendor_name",
                          },

                          {
                            label: "Vendor Location",
                            field: "vendor_location",
                          },

                          {
                            label: "GST Number",
                            field: "gst_number",
                          },
                          {
                            label: "PAN Number",
                            field: "pan_number",
                          },
                          {
                            label: "Purchase Invoice Number",
                            field: "purchase_invoice_number",
                          },
                          {
                            label: "Purchase Invoice Date",
                            field: "purchase_invoice_date",
                            type: "date",
                          },
                          {
                            label: "Vendor Invoice Number",
                            field: "voucher_invoice_number",
                          },
                          {
                            label: "Vendor Invoice Date",
                            field: "vendor_invoice_date",
                            type: "date",
                          },

                          {
                            label: "Vendor Booking Number",
                            field: "vb_number",
                          },

                          {
                            label: "Vendor Job Number",
                            field: "vendor_job_number",
                          },

                          {
                            label: "Master Shipper",
                            field: "master_shipper",
                          },

                          {
                            label: "Master Consignee",
                            field: "master_consignee",
                          },

                          {
                            label: "Master BL Number",
                            field: "master_bl_number",
                          },

                          {
                            label: "Vessel Name",
                            field: "vessel_name",
                          },

                          {
                            label: "Vessel Number",
                            field: "vessel_number",
                          },

                          {
                            label: "Voyage Number",
                            field: "voyage_number",
                          },

                          {
                            label: "Estimated Date",
                            field: "estimated_date",
                            type: "date",
                          },

                          {
                            label: "Cut Off Date",
                            field: "cut_off_date",
                            type: "date",
                          },
                        ].map(({ label, field, type }) => {
                          const lockedFields = [
                            "vendor_name",
                            "vendor_location",
                            "gst_number",
                            "pan_number",
                          ];

                          const isLocked = lockedFields.includes(field);

                          return (
                            <div className="col-md-4" key={field}>
                              <InputField
                                label={label}
                                type={type || "text"}
                                name={`purchases[${vendorIdx}].${field}`}
                                value={
                                  formik.values.purchases[vendorIdx]?.[field] ||
                                  ""
                                }
                                onChange={formik.handleChange}
                                disabled={isLocked}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* =================================================
                            CONTAINER TABLE
                        ================================================= */}

                      {containerTableData?.length > 0 && (
                        <div className="my-4">
                          <div
                            style={{
                              overflowX: "auto",
                            }}
                          >
                            <table className="table table-bordered tbllbl mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Container Type</th>

                                  <th>Weight</th>

                                  <th>Length</th>

                                  <th>Height</th>

                                  <th>Breadth</th>

                                  <th>Temperature</th>

                                  <th>Container Number</th>
                                </tr>
                              </thead>

                              <tbody>
                                {containerTableData.map((c, i) => (
                                  <tr key={c.container_id ?? i}>
                                    <td>{c.container_type || "-"}</td>

                                    <td>{c.container_weight ?? 0}</td>

                                    <td>{c.container_length ?? 0}</td>

                                    <td>{c.container_height ?? 0}</td>

                                    <td>{c.container_breadth ?? 0}</td>

                                    <td>{c.container_temperature ?? 0}</td>

                                    <td>{c.container_number || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* =================================================
                            CHARGE BREAKDOWN
                        ================================================= */}

                      <PurchaseChargeTable
                        vendorIdx={vendorIdx}
                        vendor={vendor}
                        canEdit={true}
                        chargeCodeOptions={chargeCodeOptions}
                        currencyOptions={currencyOptions}
                        containerOptions={containerOptions}
                        loadingChargeCodes={loadingChargeCodes}
                        loadingCurrencies={loadingCurrencies}
                        getPurchaseInputRow={getPurchaseInputRow}
                        updatePurchaseInputRow={updatePurchaseInputRow}
                        addPurchaseChargeRow={addPurchaseChargeRow}
                        handleRemoveItemRow={handleRemoveItemRow}
                        handleUpdateItemRow={handleUpdateItemRow}
                        fetchGstPercent={fetchGstPercent}
                        formik={formik}
                      />

                      {/* =================================================
                            TOTALS
                        ================================================= */}

                      {(() => {
                        const items =
                          formik.values.purchases[vendorIdx]?.items || [];

                        const totalTaxable = items.reduce(
                          (sum, it) => sum + (Number(it.taxable_amount) || 0),
                          0,
                        );

                        const totalGst = items.reduce(
                          (sum, it) => sum + (Number(it.gst_amount) || 0),
                          0,
                        );

                        const anyGstApplicable =
                          items.length > 0 &&
                          items.some((it) => it.gst_applicable === "Yes");

                        const row = formik.values.purchases[vendorIdx] || {};

                        const tdsPct = Number(row.tds_percentage) || 0;

                        const tdsDeduct = tdsPct > 0;

                        const tdsAmount = tdsDeduct
                          ? (totalTaxable * tdsPct) / 100
                          : 0;

                        const grandTotal = totalTaxable + totalGst - tdsAmount;

                        return (
                          <div className="d-flex justify-content-start mt-3">
                            <div
                              style={{
                                width: "420px",
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                                overflow: "hidden",
                                background: "#fff",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  padding: "14px 18px",
                                  borderBottom: "1px solid #dee2e6",
                                  fontSize: "16px",
                                }}
                              >
                                <strong>Total Taxable</strong>

                                <span>{totalTaxable.toFixed(2)} INR</span>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "14px 18px",
                                  borderBottom: "1px solid #dee2e6",
                                  fontSize: "16px",
                                }}
                              >
                                <strong>
                                  TDS Deduction ({tdsPct}
                                  %)
                                </strong>

                                <span>{tdsAmount.toFixed(2)} INR</span>
                              </div>

                              {anyGstApplicable && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "14px 18px",
                                    borderBottom: "1px solid #dee2e6",
                                    fontSize: "16px",
                                  }}
                                >
                                  <strong>GST Total</strong>

                                  <span>{totalGst.toFixed(2)} INR</span>
                                </div>
                              )}

                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  padding: "16px 18px",
                                  background: "#e5e5e5",
                                  fontSize: "16px",
                                  fontWeight: 700,
                                }}
                              >
                                <strong>Grand Total</strong>

                                <strong>{grandTotal.toFixed(2)} INR</strong>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion.Body>

            {/* =================================================
                VENDOR STATE FORMS
            ================================================= */}

            {vendors.map((vendor, vendorIndex) => {
              const vendorTotal =
                vendor.vendor_total != null && vendor.vendor_total !== ""
                  ? Number(vendor.vendor_total)
                  : (vendor.chargeRows || []).reduce(
                      (sum, c) => sum + (Number(c.amount_in_inr) || 0),
                      0,
                    );

              return (
                <VendorPurchaseForm
                  key={vendor.id}
                  vendors={vendors}
                  vendorIndex={vendorIndex}
                  vendor={vendor}
                  vendorTotal={vendorTotal}
                  purchasesLength={(formik.values.purchases || []).length}
                  vendorOptions={vendorOptions}
                  loadingVendors={loadingVendors}
                  chargeCodeOptions={chargeCodeOptions}
                  currencyOptions={currencyOptions}
                  containerOptions={containerOptions}
                  loadingChargeCodes={loadingChargeCodes}
                  loadingCurrencies={loadingCurrencies}
                  vendorInvoiceDates={vendorInvoiceDates}
                  setVendorInvoiceDates={setVendorInvoiceDates}
                  errors={errors}
                  removeVendor={removeVendor}
                  updateVendor={updateVendor}
                  updateVendorInputCharge={updateVendorInputCharge}
                  handleVendorChargeInputChange={handleVendorChargeInputChange}
                  addChargeRow={addChargeRow}
                  deleteChargeRow={deleteChargeRow}
                  handleVendorSelect={handleVendorSelect}
                  fetchGSTForChargeCode={fetchGSTForChargeCode}
                  setVendors={setVendors}
                />
              );
            })}
          </Accordion.Item>

          {/* =================================================
              SECTION 3: P&L
          ================================================= */}

          <Accordion.Item eventKey="2">
            <Accordion.Header>
              <span className="fw-bold text-black">P&L</span>
            </Accordion.Header>

            <Accordion.Body>Hello P&L</Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </form>

      {/* =====================================================
          EXISTING MODALS
      ===================================================== */}

      <ConfirmModals
        showSaveAllModal={showSaveAllModal}
        setShowSaveAllModal={setShowSaveAllModal}
        handleSaveAllPurchases={handleSaveAllPurchases}
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        showConfirm={showConfirm}
        setShowConfirm={setShowConfirm}
        isEditing={isEditing}
        loadingSubmit={loadingSubmit}
        vendors={vendors}
        formData={formData}
      />

      {/* =====================================================
          VIEW VENDOR PURCHASE MODAL
      ===================================================== */}

      {showVendorViewModal && selectedVendor && (
        <Modal
          show={showVendorViewModal}
          onHide={handleCloseVendorViewModal}
          size="xl"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {modalMode === "edit"
                ? "Edit Vendor Purchase Details"
                : "Vendor Purchase Details"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* =============================================
                  VENDOR DETAILS
              ============================================= */}

            <div className="mb-4">
              <h6 className="fw-bold text-secondary mb-3">
                Purchase Invoice Details
              </h6>

              <div className="row g-3">
                {[
                  {
                    label: "Vendor Name",
                    field: "vendor_name",
                  },

                  {
                    label: "Vendor Location",
                    field: "vendor_location",
                  },

                  {
                    label: "GST Number",
                    field: "gst_number",
                  },

                  {
                    label: "PAN Number",
                    field: "pan_number",
                  },
                  {
                    label: "Purchase Invoice Number",
                    field: "purchase_invoice_number",
                  },
                  {
                    label: "Purchase Invoice Date",
                    field: "purchase_invoice_date",
                  },
                  {
                    label: "Vendor Invoice Number",
                    field: "voucher_invoice_number",
                  },

                  {
                    label: "Vendor Invoice Date",
                    field: "vendor_invoice_date",
                    type: "date",
                  },

                  {
                    label: "Vendor Booking Number",
                    field: "vb_number",
                  },

                  {
                    label: "Vendor Job Number",
                    field: "vendor_job_number",
                  },

                  {
                    label: "Master Shipper",
                    field: "master_shipper",
                  },

                  {
                    label: "Master Consignee",
                    field: "master_consignee",
                  },

                  {
                    label: "Master BL Number",
                    field: "master_bl_number",
                  },

                  {
                    label: "Vessel Name",
                    field: "vessel_name",
                  },

                  {
                    label: "Vessel Number",
                    field: "vessel_number",
                  },

                  {
                    label: "Voyage Number",
                    field: "voyage_number",
                  },

                  {
                    label: "Estimated Date",
                    field: "estimated_date",
                    type: "date",
                  },

                  {
                    label: "Cut Off Date",
                    field: "cut_off_date",
                    type: "date",
                  },
                ].map(({ label, field, type }) => (
                  <div className="col-md-4" key={field}>
                    <InputField
                      label={label}
                      type={type || "text"}
                      name={field}
                      value={(() => {
                        const raw =
                          modalMode === "edit"
                            ? (editVendor?.[field] ?? "")
                            : (selectedVendor?.[field] ?? "");

                        // Native <input type="date"> needs yyyy-MM-dd.
                        if (type === "date" && raw) {
                          const d = new Date(raw);
                          return Number.isNaN(d.getTime())
                            ? ""
                            : d.toISOString().slice(0, 10);
                        }

                        return raw;
                      })()}
                      disabled={modalMode === "view"}
                      onChange={(e) =>
                        handleEditFieldChange(field, e.target.value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* =============================================
                  CONTAINER DETAILS
              ============================================= */}

            {containerTableData?.length > 0 && (
              <div className="my-4">
                <h6 className="fw-bold text-secondary mb-3">
                  Container Details
                </h6>

                <div
                  style={{
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-bordered tbllbl mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Container Type</th>

                        <th>Weight</th>

                        <th>Length</th>

                        <th>Height</th>

                        <th>Breadth</th>

                        <th>Temperature</th>

                        <th>Container Number</th>
                      </tr>
                    </thead>

                    <tbody>
                      {containerTableData.map((c, i) => (
                        <tr key={c.container_id ?? i}>
                          <td>{c.container_type || "-"}</td>

                          <td>{c.container_weight ?? 0}</td>

                          <td>{c.container_length ?? 0}</td>

                          <td>{c.container_height ?? 0}</td>

                          <td>{c.container_breadth ?? 0}</td>

                          <td>{c.container_temperature ?? 0}</td>

                          <td>{c.container_number || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* =============================================
                  CHARGE DETAILS
              ============================================= */}

            <div className="mt-4">
              <h6 className="fw-bold text-secondary mb-3">Charge Details</h6>
              {modalMode !== "edit" &&
              (selectedVendor?.items || []).length === 0 ? (
                <div className="alert alert-light border">
                  No charge details found.
                </div>
              ) : (
                <div
                  style={{
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-bordered tbllbl align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Charge Code</th>
                        <th>SAC Code</th>
                        <th>Currency</th>
                        <th>Rate</th>
                        <th>Quantity</th>
                        <th>Taxable Amount</th>
                        <th>GST Applicable</th>
                        <th>GST %</th>
                        <th>GST Amount</th>

                        {modalMode === "edit" && (
                          <th className="text-center">Action</th>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {/* DRAFT INPUT ROW (edit mode only) */}
                      {modalMode === "edit" && (
                        <tr>
                          <td>
                            <SelectField
                              options={chargeCodeOptions}
                              value={vendorChargeDraft.charge_code}
                              isLoading={loadingChargeCodes}
                              searchable
                              menuPortalTarget={document.body}
                              styles={selectPortalStyles}
                              onChange={(val) =>
                                handleVendorChargeDraftChange(
                                  "charge_code",
                                  val,
                                )
                              }
                            />
                          </td>
                          <td>
                            <InputField
                              name="sac_code"
                              placeholder="SAC Code"
                              value={vendorChargeDraft.sac_code || ""}
                              onChange={(e) =>
                                handleVendorChargeDraftChange(
                                  "sac_code",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td className="minselect">
                            <Select
                              options={currencyOptions}
                              value={
                                currencyOptions.find(
                                  (c) => c.value === vendorChargeDraft.currency,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleVendorChargeDraftChange(
                                  "currency",
                                  selected?.value || "",
                                )
                              }
                              isLoading={loadingCurrencies}
                              placeholder="Select"
                              menuPortalTarget={document.body}
                              styles={selectPortalStyles}
                            />
                          </td>
                          <td>
                            <InputField
                              type="number"
                              placeholder="Rate"
                              value={vendorChargeDraft.rate || ""}
                              onChange={(e) =>
                                handleVendorChargeDraftChange(
                                  "rate",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <InputField
                              type="number"
                              placeholder="Quantity"
                              value={vendorChargeDraft.quantity || ""}
                              onChange={(e) =>
                                handleVendorChargeDraftChange(
                                  "quantity",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <InputField
                              type="text"
                              value={vendorChargeDraft.taxable_amount || 0}
                              readOnly
                            />
                          </td>
                          <td className="minselect">
                            <Select
                              options={[
                                { label: "Yes", value: "Yes" },
                                { label: "No", value: "No" },
                              ]}
                              value={
                                vendorChargeDraft.gst_applicable === "No"
                                  ? { label: "No", value: "No" }
                                  : { label: "Yes", value: "Yes" }
                              }
                              onChange={(selected) =>
                                handleVendorChargeDraftChange(
                                  "gst_applicable",
                                  selected?.value || "Yes",
                                )
                              }
                              placeholder="Select"
                              menuPortalTarget={document.body}
                              styles={selectPortalStyles}
                            />
                          </td>
                          <td>
                            <InputField
                              type="number"
                              value={vendorChargeDraft.gst_percent || 0}
                              disabled={
                                vendorChargeDraft.gst_applicable !== "Yes"
                              }
                              onChange={(e) =>
                                handleVendorChargeDraftChange(
                                  "gst_percent",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <InputField
                              type="text"
                              value={vendorChargeDraft.gst_amount || 0}
                              readOnly
                            />
                          </td>
                          <td className="text-center">
                            <AddCircleOutlineIcon
                              style={{
                                cursor: "pointer",
                                color: "green",
                                fontSize: 28,
                              }}
                              onClick={handleAddVendorChargeRow}
                            />
                          </td>
                        </tr>
                      )}

                      {/* EXISTING ROWS */}
                      {(
                        (modalMode === "edit"
                          ? editVendor?.items
                          : selectedVendor?.items) || []
                      ).map((item, itemIndex) => (
                        <tr key={item.id || itemIndex}>
                          <td>
                            {modalMode === "edit" ? (
                              <SelectField
                                options={chargeCodeOptions}
                                value={item.charge_code}
                                isLoading={loadingChargeCodes}
                                searchable
                                menuPortalTarget={document.body}
                                styles={selectPortalStyles}
                                onChange={(val) =>
                                  handleEditItemChange(
                                    itemIndex,
                                    "charge_code",
                                    val,
                                  )
                                }
                              />
                            ) : (
                              item.charge_code || "-"
                            )}
                          </td>
                          <td>
                            {modalMode === "edit" ? (
                              <InputField
                                name="sac_code"
                                value={item.sac_code ?? ""}
                                onChange={(e) =>
                                  handleEditItemChange(
                                    itemIndex,
                                    "sac_code",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              item.sac_code || "-"
                            )}
                          </td>

                          <td className="minselect">
                            {modalMode === "edit" ? (
                              <Select
                                options={currencyOptions}
                                value={
                                  currencyOptions.find(
                                    (c) =>
                                      c.value ===
                                      (item.currency || item.currency_code),
                                  ) || null
                                }
                                onChange={(selected) =>
                                  handleEditItemChange(
                                    itemIndex,
                                    "currency",
                                    selected?.value || "",
                                  )
                                }
                                isLoading={loadingCurrencies}
                                placeholder="Select"
                                menuPortalTarget={document.body}
                                styles={selectPortalStyles}
                              />
                            ) : (
                              item.currency || item.currency_code || "INR"
                            )}
                          </td>

                          <td>
                            {modalMode === "edit" ? (
                              <InputField
                                type="number"
                                name="rate"
                                value={item.rate ?? 0}
                                onChange={(e) =>
                                  handleEditItemChange(
                                    itemIndex,
                                    "rate",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              (item.rate ?? 0)
                            )}
                          </td>

                          <td>
                            {modalMode === "edit" ? (
                              <InputField
                                type="number"
                                name="quantity"
                                value={item.quantity ?? 0}
                                onChange={(e) =>
                                  handleEditItemChange(
                                    itemIndex,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              (item.quantity ?? 0)
                            )}
                          </td>
                          <td>
                            {modalMode === "edit" ? (
                              <InputField
                                type="text"
                                name="taxable_amount"
                                value={item.taxable_amount ?? 0}
                                readOnly
                              />
                            ) : (
                              (item.taxable_amount ?? 0)
                            )}
                          </td>
                          <td className="minselect">
                            {modalMode === "edit" ? (
                              <Select
                                options={[
                                  { label: "Yes", value: "Yes" },
                                  { label: "No", value: "No" },
                                ]}
                                value={
                                  item.gst_applicable === "No"
                                    ? { label: "No", value: "No" }
                                    : { label: "Yes", value: "Yes" }
                                }
                                onChange={(selected) =>
                                  handleEditItemChange(
                                    itemIndex,
                                    "gst_applicable",
                                    selected?.value || "Yes",
                                  )
                                }
                                placeholder="Select"
                                menuPortalTarget={document.body}
                                styles={selectPortalStyles}
                              />
                            ) : (
                              item.gst_applicable || "No"
                            )}
                          </td>

                          <td>
                            {modalMode === "edit" ? (
                              <InputField
                                type="number"
                                name="gst_percent"
                                value={item.gst_percent ?? 0}
                                disabled={item.gst_applicable !== "Yes"}
                                onChange={(e) =>
                                  handleEditItemChange(
                                    itemIndex,
                                    "gst_percent",
                                    e.target.value,
                                  )
                                }
                              />
                            ) : (
                              (item.gst_percent ?? 0)
                            )}
                          </td>
                          <td>
                            {modalMode === "edit" ? (
                              <InputField
                                type="number"
                                name="gst_amount"
                                value={item.gst_amount ?? 0}
                                readOnly
                              />
                            ) : (
                              (item.gst_amount ?? 0)
                            )}
                          </td>

                          {modalMode === "edit" && (
                            <td className="text-center">
                              <DeleteOutlineIcon
                                style={{
                                  cursor: "pointer",
                                  color: "red",
                                  fontSize: 28,
                                }}
                                onClick={() =>
                                  handleRemoveVendorChargeRow(itemIndex)
                                }
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {vendorChargeError && (
                    <div className="text-danger mb-2">{vendorChargeError}</div>
                  )}
                </div>
              )}
            </div>

            {/* =============================================
                  TOTALS
              ============================================= */}

            {(() => {
              const items =
                (modalMode === "edit"
                  ? editVendor?.items
                  : selectedVendor?.items) || [];
              const totalTaxable = items.reduce(
                (sum, item) => sum + (Number(item.taxable_amount) || 0),
                0,
              );

              const totalGst = items.reduce(
                (sum, item) => sum + (Number(item.gst_amount) || 0),
                0,
              );

              const anyGstApplicable =
                items.length > 0 &&
                items.some((item) => item.gst_applicable === "Yes");
              const tdsPct =
                Number(
                  modalMode === "edit"
                    ? editVendor?.tds_percentage
                    : selectedVendor?.tds_percentage,
                ) || 0;
              const tdsAmount = tdsPct > 0 ? (totalTaxable * tdsPct) / 100 : 0;

              const grandTotal = totalTaxable + totalGst - tdsAmount;

              return (
                <div className="d-flex justify-content-start mt-4">
                  <div
                    style={{
                      width: "420px",

                      border: "1px solid #dee2e6",

                      borderRadius: "6px",

                      overflow: "hidden",

                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",

                        justifyContent: "space-between",

                        padding: "14px 18px",

                        borderBottom: "1px solid #dee2e6",

                        fontSize: "16px",
                      }}
                    >
                      <strong>Total Taxable</strong>

                      <span>{totalTaxable.toFixed(2)} INR</span>
                    </div>

                    <div
                      style={{
                        display: "flex",

                        justifyContent: "space-between",

                        padding: "14px 18px",

                        borderBottom: "1px solid #dee2e6",

                        fontSize: "16px",
                      }}
                    >
                      <strong>
                        TDS Deduction ({tdsPct}
                        %)
                      </strong>

                      <span>{tdsAmount.toFixed(2)} INR</span>
                    </div>

                    {anyGstApplicable && (
                      <div
                        style={{
                          display: "flex",

                          justifyContent: "space-between",

                          padding: "14px 18px",

                          borderBottom: "1px solid #dee2e6",

                          fontSize: "16px",
                        }}
                      >
                        <strong>GST Total</strong>

                        <span>{totalGst.toFixed(2)} INR</span>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",

                        justifyContent: "space-between",

                        padding: "16px 18px",

                        background: "#e5e5e5",

                        fontSize: "16px",

                        fontWeight: 700,
                      }}
                    >
                      <strong>Grand Total</strong>

                      <strong>{grandTotal.toFixed(2)} INR</strong>
                    </div>
                  </div>
                </div>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            {modalMode === "edit" && (
              <Button
                variant="primary"
                disabled={saving}
                onClick={handleSaveVendorEdit}
              >
                {saving ? "Updating..." : "Update"}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default Commercial;
